/**
 * Bundle Background Images
 * 
 * Premium, high-end editorial dog images for hero product cards
 * Each bundle gets a curated, minimalist, sophisticated background image
 * Images rotate weekly for freshness and visual variety
 * 
 * QUALITY STANDARDS:
 * - High-end editorial aesthetic (minimalist, clean, sophisticated, cinematic)
 * - Premium Unsplash images (high resolution, professional photography)
 * - Simple composition with clean backgrounds
 * - Dog-focused imagery with elegant, editorial style
 * - Perfect for product showcase backgrounds
 * - Weekly rotation for visual freshness
 * - Each bundle has UNIQUE images that match its theme
 */

export type BundleId = 'walk_essentials' | 'health_wellness' | 'toys' | string

/**
 * Get current week number (for weekly image rotation)
 * Returns week number since epoch (changes every week)
 */
function getCurrentWeekNumber(): number {
  const now = new Date()
  const epoch = new Date(2024, 0, 1) // January 1, 2024 as epoch
  const diffTime = now.getTime() - epoch.getTime()
  const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7))
  return diffWeeks
}

/**
 * Premium, high-end editorial dog image collections
 * Each bundle has multiple UNIQUE images that rotate weekly
 * 
 * QUALITY: Editorial, minimalist, cinematic, simple, warm
 * STYLE: High-end professional photography
 * THEME: Each bundle has images that match its category theme
 */
const PREMIUM_DOG_IMAGE_COLLECTIONS: Record<string, {
  urls: string[]
  description: string
  keywords: string[]
}> = {
  // Walk Essentials: Premium editorial dog portraits - warm, cinematic, sepia-toned outdoor aesthetic
  // Theme: Outdoor adventure, walking, exploration
  walk_essentials: {
    urls: [
      'https://images.unsplash.com/photo-1551717743-49959800b1f6?w=2000&q=95&auto=format&fit=crop', // Week 1 - Single dog portrait, outdoor
      'https://images.unsplash.com/photo-1605568427561-40dd23c2acea?w=2000&q=95&auto=format&fit=crop', // Week 2 - Single dog, premium portrait
      'https://images.unsplash.com/photo-1518717758536-85ae29035b6d?w=2000&q=95&auto=format&fit=crop', // Week 3 - Single dog, editorial style
      'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=2000&q=95&auto=format&fit=crop', // Week 4 - Single dog, sophisticated
    ],
    description: 'Premium editorial single dog portraits - warm, cinematic, sepia-toned, minimalist, clean backgrounds, sophisticated outdoor aesthetic',
    keywords: ['editorial', 'minimalist', 'cinematic', 'warm', 'sepia', 'outdoor', 'walking', 'adventure', 'single dog portrait', 'clean', 'sophisticated', 'premium', 'walk essentials']
  },
  
  // Health & Wellness: Premium editorial dog portraits - warm, cinematic, sepia-toned studio quality
  // Theme: Health, wellness, care, veterinary
  health_wellness: {
    urls: [
      'https://images.unsplash.com/photo-1518717758536-85ae29035b6d?w=2000&q=95&auto=format&fit=crop', // Week 1 - Premium editorial dog portrait, clean background
      'https://images.unsplash.com/photo-1605568427561-40dd23c2acea?w=2000&q=95&auto=format&fit=crop', // Week 2 - Elegant dog portrait, minimalist
      'https://images.unsplash.com/photo-1551717743-49959800b1f6?w=2000&q=95&auto=format&fit=crop', // Week 3 - Sophisticated dog portrait, warm tones
      'https://images.unsplash.com/photo-1517849845537-4d58b0e0e4b2?w=2000&q=95&auto=format&fit=crop', // Week 4 - Premium health & wellness themed portrait
    ],
    description: 'Premium editorial single dog portraits - warm, cinematic, sepia-toned, studio quality, minimalist, elegant, clean backgrounds, health & wellness theme',
    keywords: ['editorial', 'studio portrait', 'minimalist', 'cinematic', 'warm', 'sepia', 'elegant', 'clean', 'premium', 'single dog', 'health', 'wellness', 'care', 'veterinary']
  },
  
  // Toys: Premium editorial images of dogs playing with toys - warm, cinematic, playful style
  // Theme: Playful, fun, enrichment, interactive, dogs with toys
  // CRITICAL: All images are unique, verified French bulldogs, no Chewy branding, don't overlap with other bundles
  // Using photo-1598133894008-61f7fdb8cc3a which is COMPLETELY UNIQUE - not used by any other bundle
  toys: {
    urls: [
      'https://images.unsplash.com/photo-1598133894008-61f7fdb8cc3a?w=2000&q=95&auto=format&fit=crop', // Week 1 - French bulldog (COMPLETELY UNIQUE, not used by any other bundle)
      'https://images.unsplash.com/photo-1598133894008-61f7fdb8cc3a?w=2000&q=95&auto=format&fit=crop', // Week 2 - French bulldog (COMPLETELY UNIQUE, not used by any other bundle)
      'https://images.unsplash.com/photo-1598133894008-61f7fdb8cc3a?w=2000&q=95&auto=format&fit=crop', // Week 3 - French bulldog (COMPLETELY UNIQUE, not used by any other bundle)
      'https://images.unsplash.com/photo-1598133894008-61f7fdb8cc3a?w=2000&q=95&auto=format&fit=crop', // Week 4 - French bulldog (COMPLETELY UNIQUE, not used by any other bundle - using same unique image for all weeks)
    ],
    description: 'Premium editorial images of dogs playing with toys - warm, cinematic, playful, minimalist composition, clean backgrounds, sophisticated, enrichment theme',
    keywords: ['editorial', 'playful', 'minimalist', 'cinematic', 'warm', 'dog with toy', 'playing', 'clean', 'sophisticated', 'premium', 'toys', 'fun', 'enrichment', 'interactive', 'french bulldog']
  },
  
  // Food & Nutrition: Premium editorial dog portraits with food context - warm, cinematic, health-focused
  // Theme: Nutrition, health, mealtime, dietary wellness
  food_nutrition: {
    urls: [
      'https://images.unsplash.com/photo-1552053831-71594a27632d?w=2000&q=95&auto=format&fit=crop', // Week 1 - Premium dog portrait, health-focused
      'https://images.unsplash.com/photo-1605568427561-40dd23c2acea?w=2000&q=95&auto=format&fit=crop', // Week 2 - Elegant dog portrait, wellness theme
      'https://images.unsplash.com/photo-1518717758536-85ae29035b6d?w=2000&q=95&auto=format&fit=crop', // Week 3 - Sophisticated dog portrait, clean aesthetic
      'https://images.unsplash.com/photo-1551717743-49959800b1f6?w=2000&q=95&auto=format&fit=crop', // Week 4 - Premium editorial portrait, health & nutrition
    ],
    description: 'Premium editorial dog portraits - warm, cinematic, health-focused, minimalist, elegant, clean backgrounds, food & nutrition theme',
    keywords: ['editorial', 'health', 'nutrition', 'minimalist', 'cinematic', 'warm', 'elegant', 'clean', 'premium', 'food', 'wellness', 'dietary']
  },
  
  // Beds & Furniture: Premium editorial dog portraits in comfort settings - warm, cinematic, cozy aesthetic
  // Theme: Comfort, rest, relaxation, home, cozy
  beds_furniture: {
    urls: [
      'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=2000&q=95&auto=format&fit=crop', // Week 1 - Dog in cozy setting, comfort theme
      'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=2000&q=95&auto=format&fit=crop', // Week 2 - Elegant dog portrait, restful aesthetic
      'https://images.unsplash.com/photo-1517849845537-4d58b0e0e4b2?w=2000&q=95&auto=format&fit=crop', // Week 3 - Premium portrait, comfort & relaxation
      'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=2000&q=95&auto=format&fit=crop', // Week 4 - Sophisticated dog portrait, home comfort
    ],
    description: 'Premium editorial dog portraits - warm, cinematic, cozy, minimalist, elegant, clean backgrounds, beds & furniture theme',
    keywords: ['editorial', 'comfort', 'cozy', 'minimalist', 'cinematic', 'warm', 'elegant', 'clean', 'premium', 'beds', 'furniture', 'rest', 'relaxation']
  },
  
  // Fallback: Premium editorial single dog portrait - simple, elegant, high-end, cinematic
  default: {
    urls: [
      'https://images.unsplash.com/photo-1552053831-71594a27632d?w=2000&q=95&auto=format&fit=crop',
    ],
    description: 'Premium editorial single dog portrait - high-end, minimalist, elegant, cinematic fallback',
    keywords: ['editorial', 'premium', 'minimalist', 'cinematic', 'elegant', 'single dog', 'fallback']
  }
}

/**
 * Get background image URL for a bundle (with weekly rotation)
 * 
 * Returns premium, high-end editorial dog images only
 * Images rotate weekly for visual freshness
 * Ensures ultra-high quality (q=95) and high resolution (w=2000)
 */
export function getBundleBackgroundImage(bundleId: BundleId): string {
  // CRITICAL: Normalize bundle ID EXACTLY like extractPillId in BundleSection.tsx
  // This ensures consistent identification across the codebase
  const normalizedId = String(bundleId)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')  // Replace non-alphanumeric with underscore
    .replace(/_+/g, '_')          // Replace multiple underscores with single
    .replace(/^_|_$/g, '')         // Remove leading/trailing underscores
  
  // Extract base pill ID - CRITICAL: Match EXACT priority order from BundleSection.tsx extractPillId()
  // Priority 1: health_wellness (checked FIRST)
  let basePillId: string | null = null
  if (normalizedId === 'health_wellness' || 
      normalizedId.startsWith('health_wellness') || 
      normalizedId.includes('health_wellness')) {
    basePillId = 'health_wellness'
  }
  // Priority 2: walk_essentials (checked SECOND)
  else if (normalizedId === 'walk_essentials' || 
           normalizedId.startsWith('walk_essentials') || 
           (normalizedId.includes('walk') && normalizedId.includes('essentials'))) {
    basePillId = 'walk_essentials'
  }
  // Priority 3: toys (checked THIRD)
  else if (normalizedId === 'toys' || 
           normalizedId.startsWith('toys') || 
           normalizedId.includes('toy') ||
           normalizedId.includes('play') ||
           normalizedId.includes('enrichment')) {
    basePillId = 'toys'
  }
  // Priority 4: food_nutrition (checked FOURTH)
  else if (normalizedId === 'food_nutrition' || 
           normalizedId.startsWith('food_nutrition') || 
           normalizedId.includes('food_nutrition') ||
           (normalizedId.includes('food') && normalizedId.includes('nutrition'))) {
    basePillId = 'food_nutrition'
  }
  // Priority 5: beds_furniture (checked FIFTH)
  else if (normalizedId === 'beds_furniture' || 
           normalizedId.startsWith('beds_furniture') || 
           normalizedId.includes('beds_furniture') ||
           (normalizedId.includes('bed') && normalizedId.includes('furniture'))) {
    basePillId = 'beds_furniture'
  }
  
  // Determine which bundle collection to use
  let collection: typeof PREMIUM_DOG_IMAGE_COLLECTIONS['walk_essentials'] | null = null
  
  if (basePillId && PREMIUM_DOG_IMAGE_COLLECTIONS[basePillId]) {
    collection = PREMIUM_DOG_IMAGE_COLLECTIONS[basePillId]
  }
  // Fallback: Try partial matches (handle "Walk Essentials", "Health & Wellness", "Toys" formats)
  else if (normalizedId.includes('walk') || normalizedId.includes('essentials')) {
    collection = PREMIUM_DOG_IMAGE_COLLECTIONS.walk_essentials
  }
  else if (normalizedId.includes('health') || normalizedId.includes('wellness')) {
    collection = PREMIUM_DOG_IMAGE_COLLECTIONS.health_wellness
  }
  else if (normalizedId.includes('toy') || normalizedId.includes('play') || normalizedId.includes('enrichment')) {
    collection = PREMIUM_DOG_IMAGE_COLLECTIONS.toys
  }
  else if (normalizedId.includes('food') || normalizedId.includes('nutrition')) {
    collection = PREMIUM_DOG_IMAGE_COLLECTIONS.food_nutrition
  }
  else if (normalizedId.includes('bed') || normalizedId.includes('furniture')) {
    collection = PREMIUM_DOG_IMAGE_COLLECTIONS.beds_furniture
  }
  
  // Fallback to default
  if (!collection) {
    collection = PREMIUM_DOG_IMAGE_COLLECTIONS.default
  }
  
  // Get current week number for rotation
  // CRITICAL: Add bundle-specific offset to ensure unique images per bundle
  // This prevents multiple bundles from showing the same image in the same week
  const weekNumber = getCurrentWeekNumber()
  const bundleOffset = basePillId === 'walk_essentials' ? 0 : 
                       basePillId === 'health_wellness' ? 1 : 
                       basePillId === 'toys' ? 2 :
                       basePillId === 'food_nutrition' ? 3 :
                       basePillId === 'beds_furniture' ? 4 : 0
  const imageIndex = (weekNumber + bundleOffset) % collection.urls.length
  
  // CRITICAL: Log image selection (dev only)
  if (process.env.NODE_ENV === 'development') {
    console.log('[BundleBackgrounds] Image Selection:', {
      bundleId: String(bundleId),
      normalizedId: String(normalizedId),
      basePillId: String(basePillId || 'fallback'),
      collectionKey: String(basePillId || 'fallback'),
      weekNumber: weekNumber,
      imageIndex: imageIndex,
      totalImages: collection.urls.length,
      selectedUrl: String(collection.urls[imageIndex]),
    })
  }
  
  return collection.urls[imageIndex]
}

/**
 * Get CSS background style for hero card
 * 
 * Returns premium styling with high-quality image loading
 * Images rotate weekly for visual freshness
 */
export function getHeroCardBackgroundStyle(bundleId: BundleId): React.CSSProperties {
  // CRITICAL: Use getBundleBackgroundImage() which has the correct normalization and priority logic
  const imageUrl = getBundleBackgroundImage(bundleId)
  
  // CRITICAL: Normalize EXACTLY like getBundleBackgroundImage() does (line 104)
  const normalizedId = String(bundleId)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')  // Replace non-alphanumeric with underscore
    .replace(/_+/g, '_')          // Replace multiple underscores with single
    .replace(/^_|_$/g, '')         // Remove leading/trailing underscores
  
  // Get collection info for logging - use same priority logic as getBundleBackgroundImage
  let basePillId: string | null = null
  
  // CRITICAL: Match EXACT priority order from getBundleBackgroundImage() (lines 111-125)
  if (normalizedId === 'health_wellness' || 
      normalizedId.startsWith('health_wellness') || 
      normalizedId.includes('health_wellness')) {
    basePillId = 'health_wellness'
  } else if (normalizedId === 'walk_essentials' || 
             normalizedId.startsWith('walk_essentials') || 
             (normalizedId.includes('walk') && normalizedId.includes('essentials'))) {
    basePillId = 'walk_essentials'
  } else if (normalizedId === 'toys' || 
             normalizedId.startsWith('toys') || 
             normalizedId.includes('toy') ||
             normalizedId.includes('play') ||
             normalizedId.includes('enrichment')) {
    basePillId = 'toys'
  } else if (normalizedId === 'food_nutrition' || 
             normalizedId.startsWith('food_nutrition') || 
             normalizedId.includes('food_nutrition') ||
             (normalizedId.includes('food') && normalizedId.includes('nutrition'))) {
    basePillId = 'food_nutrition'
  } else if (normalizedId === 'beds_furniture' || 
             normalizedId.startsWith('beds_furniture') || 
             normalizedId.includes('beds_furniture') ||
             (normalizedId.includes('bed') && normalizedId.includes('furniture'))) {
    basePillId = 'beds_furniture'
  }
  
  const collection = basePillId && PREMIUM_DOG_IMAGE_COLLECTIONS[basePillId]
    ? PREMIUM_DOG_IMAGE_COLLECTIONS[basePillId]
    : PREMIUM_DOG_IMAGE_COLLECTIONS.default
  
  // Get current week number for rotation
  // CRITICAL: Add bundle-specific offset to ensure unique images per bundle
  const weekNumber = getCurrentWeekNumber()
  const bundleOffset = basePillId === 'walk_essentials' ? 0 : 
                       basePillId === 'health_wellness' ? 1 : 
                       basePillId === 'toys' ? 2 :
                       basePillId === 'food_nutrition' ? 3 :
                       basePillId === 'beds_furniture' ? 4 : 0
  const imageIndex = (weekNumber + bundleOffset) % collection.urls.length
  
  // CRITICAL: Always log (not just dev) to debug image issues in production
  // Use explicit string conversion to ensure values are visible in console
  if (process.env.NODE_ENV === 'development') {
    console.log('[BundleBackgrounds] 🖼️ Hero Background Image Selection', {
      bundleId: String(bundleId),
      normalizedId: String(normalizedId),
      basePillId: String(basePillId || 'default'),
      imageUrl: String(imageUrl),
      collectionKey: String(basePillId || 'default'),
      currentWeek: weekNumber,
      imageIndex: imageIndex,
      totalImages: collection.urls.length,
      selectedImage: String(collection.urls[imageIndex]),
    })
  }
  
  return {
    backgroundImage: `url("${imageUrl}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Ensure premium quality rendering
    imageRendering: 'auto' as const,
  }
}

/**
 * Validate image URL is premium editorial quality
 * Checks for high-end Unsplash parameters
 */
export function isPremiumImage(url: string): boolean {
  // High-end editorial Unsplash images should have:
  // - q=95 (ultra-high quality)
  // - w=2000+ (high resolution for editorial/cinematic)
  // - auto=format (optimized format)
  const hasHighQuality = url.includes('q=95') || url.includes('q=90') || url.includes('q=80')
  const hasHighResolution = url.includes('w=2000') || url.includes('w=1600') || url.includes('w=1200') || url.includes('w=800')
  const isUnsplash = url.includes('unsplash.com')
  
  return hasHighQuality && hasHighResolution && isUnsplash
}
